import { useState, useEffect } from 'react'
import { UploadDocuments } from '../UploadDocuments'
import { SelectFolder } from '../SelectFolder'
import { SelectEmployee } from '../SelectEmployee'
import { SelectPairing } from '../SelectPairing'
import { IoCloseSharp } from 'react-icons/io5'

const allowedTypes = [
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/plain',
	'image/png',
	'image/jpeg',
	'image/jpg',
	'image/gif',
	'image/webp',
]

export const ModalDocument = ({ isOpen, onClose, folders = [], onSubmit, employeesList: originalEmployeesList, employeeView = false }) => {
	const [step, setStep] = useState(1)
	const [selectedFiles, setSelectedFiles] = useState([])
	const [selectedFolder, setSelectedFolder] = useState(null)
	const [selectedEmployees, setSelectedEmployees] = useState([])
	const [pairings, setPairings] = useState([])
	const [fileError, setFileError] = useState('')

	const userId = JSON.parse(localStorage.getItem('user'))?.id
	const employeesList = employeeView ? [userId] : originalEmployeesList

	const handleFolderSelect = folderId => setSelectedFolder(folderId)
	const handleEmployeeSelect = employees => setSelectedEmployees(employees)

	const handleNext = () => setStep(prev => prev + 1)
	const handleBack = () => {
		setStep(prev => prev - 1)
		setPairings([])
	}

	const handleClose = () => {
		setStep(1)
		setSelectedFiles([])
		setSelectedFolder(null)
		setSelectedEmployees([])
		setPairings([])
		setFileError('')
		onClose()
	}

	const handleSelectFiles = files => {
		let error = ''
		const validFiles = []
		for (const file of files) {
			if (!allowedTypes.includes(file.type)) {
				error = 'Archivo inválido. Tipos permitidos: PDF, DOCX, PPTX, XLSX, TXT o imagen.'
				continue
			}
			if (file.size > 10 * 1024 * 1024) {
				error = 'Tamaño máximo de archivo: 10 MB.'
				continue
			}
			validFiles.push(file)
		}
		setFileError(error)
		setSelectedFiles(validFiles)
	}

	useEffect(() => {
		if (isOpen) {
			const folderObj = folders.find(f => f.id === selectedFolder)
			const isCommon = folderObj && folderObj.private === false
			if (isCommon) {
				const ids = employeesList.map(e => (typeof e === 'object' ? e.id : e))
				setSelectedEmployees(ids)
			} else {
				setSelectedEmployees([])
			}
		}
	}, [isOpen, folders, selectedFolder])

	useEffect(() => {
		setPairings([])
	}, [selectedFolder])

	function findEquivalentId(data, sourceId, targetEmployeeId) {
		const sourceItem = data.find(item => item.id === sourceId)
		if (!sourceItem) return null

		if (sourceItem.employees?.some(emp => emp.id === targetEmployeeId)) {
			return sourceId
		}

		const sourceName = sourceItem.name
		const sourceParentId = sourceItem.parentId

		const directMatch = data.find(
			item =>
				item.name === sourceName &&
				item.parentId === sourceParentId &&
				item.id !== sourceId &&
				item.employees?.some(emp => emp.id === targetEmployeeId)
		)

		if (directMatch) return directMatch.id

		if (sourceParentId !== null) {
			const sourceParent = data.find(item => item.id === sourceParentId)
			if (!sourceParent) return null

			const equivalentParent = data.find(
				item => item.name === sourceParent.name && item.parentId === sourceParent.parentId && item.employees?.some(emp => emp.id === targetEmployeeId)
			)

			if (!equivalentParent) return null

			const childEquivalent = data.find(
				item => item.name === sourceName && item.parentId === equivalentParent.id && item.employees?.some(emp => emp.id === targetEmployeeId)
			)

			return childEquivalent?.id || null
		}

		return null
	}

	const handleFinalSubmit = () => {
		const companyId = JSON.parse(localStorage.getItem('user')).id

		const folderObj = folders.find(f => f.id === selectedFolder)
		const isIndividual = folderObj && folderObj.private === true

		const dataRH = selectedFiles.map((file, idx) => {
			const employeeId = pairings[idx] || selectedEmployees[idx] || null

			const folderId = findEquivalentId(folders, selectedFolder, employeeId)

			const obj = {
				fileName: file.name,
				companyId: companyId,
				folderId: isIndividual ? folderId : pairings[idx] || selectedFolder,
			}
			if (selectedEmployees && selectedEmployees.length > 0) {
				obj.employeeIds = pairings[idx] || selectedEmployees
			}
			return obj
		})

		const dataEmp = selectedFiles.map((file, idx) => {
			const obj = {
				companyId: JSON.parse(localStorage.getItem('user')).companyId,
				fileName: file.name,
				employeeId: JSON.parse(localStorage.getItem('user')).id,
				folderId: selectedFolder,
			}
			return obj
		})

		const data = employeeView ? dataEmp : dataRH

		onSubmit(data, selectedFiles)
		handleClose()
	}

	if (!isOpen) return null

	return (
		<div className='tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center'>
			<div className='tw-bg-white tw-rounded-xl tw-shadow-lg tw-h-[682px] tw-w-[816px] tw-p-6 tw-relative'>
				<IoCloseSharp
					onClick={handleClose}
					className='tw-absolute tw-top-3 tw-right-3 tw-text-2xl tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer'
					title='Fechar'
				/>
				<div className='tw-text-lg tw-font-medium tw-mb-6'>Nuevo documento</div>

				{step === 1 && (
					<>
						<div className='tw-w-full'>
							<div className='tw-mb-6'>
								<div className='tw-font-bold tw-mb-2'>Paso 1: Subir Documento(s)</div>
								<UploadDocuments onSelectFiles={handleSelectFiles} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} />
								{fileError && <div className='tw-text-error-500 tw-text-xs tw-mt-2'>{fileError}</div>}
								<div className='tw-text-xs tw-text-gray-500 tw-mt-2'>Permitido: PDF, DOCX, PPTX, XLSX, TXT o imagen. Tamaño máximo: 10 MB.</div>
							</div>
						</div>
						<div className='tw-absolute tw-bottom-6 tw-right-6 tw-flex tw-gap-3'>
							<div onClick={handleClose} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Cancelar
							</div>
							<div
								onClick={handleNext}
								className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
									selectedFiles.length === 0 ? 'tw-opacity-50 tw-pointer-events-none' : ''
								}`}
							>
								Seguinte
							</div>
						</div>
					</>
				)}

				{step === 2 && (
					<>
						<div className='tw-w-full'>
							<div className='tw-mb-6'>
								<div className='tw-font-bold tw-mb-2'>Paso 2: Seleccionar Carpeta</div>
								<SelectFolder folders={folders} onSelect={handleFolderSelect} selectedFolder={selectedFolder} />
							</div>
							{(() => {
								const folderObj = folders.find(f => f.id === selectedFolder)
								const isPrivate = folderObj && folderObj.private === true

								if (isPrivate && !employeeView) {
									return (
										<div className='tw-mb-6'>
											<div className='tw-font-bold tw-mb-2'>Paso 3: Seleccionar Colaborador(es)</div>
											<div className='tw-text-sm tw-text-gray-500 tw-mb-5'>
												Seleccione los colaboradores para acceder a los documentos. En la siguiente pantalla podrá asociar cada documento con su
												respectivo colaborador o carpeta. En el caso de carpeta individual, el número de colaboradores no puede ser superior al número
												de documentos.
											</div>

											<SelectEmployee employees={employeesList} onSelect={handleEmployeeSelect} selectedEmployees={selectedEmployees} />
										</div>
									)
								}
								return null
							})()}
						</div>
						<div className='tw-absolute tw-bottom-6 tw-right-6 tw-flex tw-gap-3'>
							<div onClick={handleBack} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Volver
							</div>
							{(() => {
								const folderObj = folders.find(f => f.id === selectedFolder)
								const isPrivate = folderObj && folderObj.private === true

								let disabled
								if (employeeView) {
									disabled = !selectedFolder
								} else if (isPrivate) {
									disabled = !selectedFolder || selectedEmployees.length === 0 || selectedEmployees.length > selectedFiles.length
								} else {
									disabled = !selectedFolder
								}

								return employeeView && isPrivate ? (
									<div
										onClick={!disabled ? handleFinalSubmit : undefined}
										className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
											disabled ? 'tw-opacity-50 tw-pointer-events-none' : ''
										}`}
									>
										Subir
									</div>
								) : (
									<div
										onClick={!disabled ? handleNext : undefined}
										className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
											disabled ? 'tw-opacity-50 tw-pointer-events-none' : ''
										}`}
									>
										Seguinte
									</div>
								)
							})()}
						</div>
					</>
				)}

				{step === 3 && (
					<>
						<div className='tw-w-full'>
							<div className='tw-mb-6'>
								<div className='tw-font-bold tw-mb-2'>
									{(() => {
										const folderObj = folders.find(f => f.id === selectedFolder)
										const isPrivate = folderObj && folderObj.private === true
										return isPrivate ? 'Paso 4: Pareamento' : 'Paso 3: Pareamento'
									})()}
								</div>
								<SelectPairing
									files={selectedFiles}
									folders={folders}
									employees={employeesList}
									selectedFolder={selectedFolder}
									selectedEmployees={selectedEmployees}
									value={pairings}
									onChange={setPairings}
								/>
							</div>
						</div>
						<div className='tw-absolute tw-bottom-6 tw-right-6 tw-flex tw-gap-3'>
							<div onClick={handleBack} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Volver
							</div>
							<div
								onClick={handleFinalSubmit}
								className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg`}
							>
								Finalizar
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
